import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useRef, useMemo } from 'react';
import { Box, Typography, Button, Paper, CircularProgress, Alert } from '@mui/material';
import { useForm } from 'react-hook-form';
import { SingleSelect } from '@ui/components/common/Autocomplete';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getCompanyThemeAsync, updateCompanyThemeAsync } from '@application/features/companyTheme/companyThemeThunk';
import { setThemeApplied } from '@application/features/companyTheme/companyThemeSlice';
import { toast } from 'react-toastify';
import { ThemeMode } from '@application/features/companyTheme/models/company-theme';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid2';
const CompanyThemeTab = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { currentUser } = useAppSelector((state) => state.auth);
    const { selectedCompanyId } = useAppSelector((state) => state.company);
    const { currentTheme } = useAppSelector((state) => state.companyTheme);
    const [isLoading, setIsLoading] = useState(false);
    const loadedCompanyIdRef = useRef(null);
    const themeLoadedRef = useRef(false);
    // Determine which company to use for theme settings - use first selected company, or fall back to primary
    const activeCompanyId = selectedCompanyId && selectedCompanyId.length > 0
        ? selectedCompanyId[0]
        : currentUser?.primaryCompanyId;
    // Color options (using hex values from palette.ts)
    const colorOptions = useMemo(() => [
        { label: 'Sky Blue', value: '#00B6ED' },
        { label: 'Ocean Blue', value: '#3192D0' },
        { label: 'Dark Blue', value: '#2D3077' },
        { label: 'Midnight Blue', value: '#00244D' },
        { label: 'Red', value: '#E52322' },
        { label: 'Orange', value: '#E94E1B' },
        { label: 'Yellow', value: '#FFDD00' },
        { label: 'Green', value: '#76B82A' }
    ], []);
    // Font family options
    const fontFamilyOptions = useMemo(() => [
        { label: 'Aeonik', value: 'Aeonik' },
        { label: 'SuisseIntl', value: 'SuisseIntl' },
        { label: 'Onest', value: 'Onest' },
        { label: 'Archivo', value: 'Archivo' },
        { label: 'ArchivoSemiExpanded', value: 'ArchivoSemiExpanded' },
        { label: 'InstrumentSans', value: 'InstrumentSans' },
        { label: 'Scandium', value: 'Scandium' }
    ], []);
    const emptyTheme = useMemo(() => ({
        primaryColor: '',
        secondaryColor: '',
        backgroundColor: '',
        backgroundImageUrl: '',
        fontFamily: '',
        fontSize: '',
        fontStyle: '',
        errorColor: '',
        warningColor: '',
        successColor: '',
        infoColor: '',
        mode: ThemeMode.Light
    }), []);
    const { control, handleSubmit, reset, formState: { isDirty } } = useForm({
        defaultValues: {
            primaryColor: '',
            secondaryColor: '',
            backgroundColor: '',
            backgroundImageUrl: '',
            fontFamily: '',
            fontSize: '',
            fontStyle: '',
            errorColor: '',
            warningColor: '',
            successColor: '',
            infoColor: '',
            mode: ThemeMode.Light
        }
    });
    // Load current theme on mount or when company changes
    useEffect(() => {
        if (activeCompanyId && loadedCompanyIdRef.current !== activeCompanyId) {
            loadedCompanyIdRef.current = activeCompanyId;
            themeLoadedRef.current = false;
            // Reset form to empty values first
            reset(emptyTheme);
            dispatch(getCompanyThemeAsync(activeCompanyId))
                .unwrap()
                .then(() => {
                // Set theme as applied after successful fetch
                dispatch(setThemeApplied(true));
            })
                .catch(() => {
                // Even if fetch fails, mark as applied to avoid reverting to base theme
                dispatch(setThemeApplied(true));
            });
        }
    }, [activeCompanyId, dispatch, reset, emptyTheme]);
    // Update form when theme is loaded
    useEffect(() => {
        // Only update if we're looking at the same company we just loaded
        if (loadedCompanyIdRef.current === activeCompanyId && currentTheme) {
            // Mark as loaded to prevent future resets unless company changes
            if (!themeLoadedRef.current) {
                themeLoadedRef.current = true;
            }
            // If theme exists and has values, use it; otherwise use empty theme
            if (Object.values(currentTheme).some((val) => val !== null && val !== undefined && val !== '')) {
                reset(currentTheme);
            }
            else {
                reset(emptyTheme);
            }
        }
    }, [currentTheme, activeCompanyId, reset, emptyTheme]);
    const onSubmit = async (data) => {
        if (!activeCompanyId) {
            toast.error(t('settings.companyTheme.noCompanySelected'));
            return;
        }
        setIsLoading(true);
        try {
            await dispatch(updateCompanyThemeAsync({
                companyId: activeCompanyId,
                ...data
            })).unwrap();
            // Set theme as applied after successful update
            dispatch(setThemeApplied(true));
            toast.success(t('settings.companyTheme.updateSuccess'));
        }
        catch (error) {
            toast.error(error?.message || t('settings.companyTheme.updateError'));
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleReset = () => {
        reset(emptyTheme);
    };
    if (!activeCompanyId) {
        return (_jsx(Box, { sx: { p: 3 }, children: _jsx(Alert, { severity: 'warning', children: t('settings.companyTheme.noCompanySelected') }) }));
    }
    return (_jsxs(Box, { sx: { p: 3 }, children: [_jsx(Typography, { variant: 'h4', gutterBottom: true, children: t('settings.companyTheme.title') }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', sx: { mb: 3 }, children: t('settings.companyTheme.description') }), _jsx("form", { onSubmit: handleSubmit(onSubmit), children: _jsxs(Grid, { container: true, spacing: 3, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Paper, { sx: { p: 3, borderRadius: 4 }, children: [_jsx(Typography, { variant: 'h6', gutterBottom: true, children: t('settings.companyTheme.colorSettings') }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsx(SingleSelect, { name: 'primaryColor', control: control, label: t('settings.companyTheme.primaryColor'), placeholder: String(t('settings.companyTheme.primaryColorPlaceholder')), options: colorOptions, bg: 'grey8' }) }), _jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsx(SingleSelect, { name: 'secondaryColor', control: control, label: t('settings.companyTheme.secondaryColor'), placeholder: String(t('settings.companyTheme.secondaryColorPlaceholder')), options: colorOptions, bg: 'grey8' }) }), _jsx(Typography, { variant: 'h6', gutterBottom: true, children: t('settings.companyTheme.typographySettings') }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(SingleSelect, { name: 'fontFamily', control: control, label: t('settings.companyTheme.fontFamily'), placeholder: String(t('settings.companyTheme.fontFamilyPlaceholder')), options: fontFamilyOptions, bg: 'grey8' }) })] })] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { sx: { display: 'flex', gap: 2, justifyContent: 'flex-end' }, children: [_jsx(Button, { variant: 'outlined', onClick: handleReset, disabled: !isDirty || isLoading, children: t('settings.companyTheme.reset') }), _jsx(Button, { type: 'submit', variant: 'contained', disabled: !isDirty || isLoading, startIcon: isLoading && _jsx(CircularProgress, { size: 20 }), children: isLoading
                                            ? t('settings.companyTheme.saving')
                                            : t('settings.companyTheme.saveTheme') })] }) })] }) })] }));
};
export default CompanyThemeTab;
